/**
 * Adapters API
 * 
 * Functions for managing adapters.
 */

import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { AdapterRecord } from './types';

export interface ProviderSettingsRecord {
  id?: string;
  scope: 'global' | 'adapter';
  adapter_slug?: string | null;
  operation_type: 'discovery' | 'detail';
  use_auto: boolean;
  enabled_providers: string[];
  provider_preference: string[];
  retry_count: number;
  timeout_ms: number;
  metadata?: Record<string, unknown>;
}

export interface AdapterStatsSnapshotRecord {
  id: string;
  run_id: string;
  adapter_slug: string;
  benchmark_group: string;
  total_properties: number;
  total_snapshots: number;
  unique_properties: number;
  overlap_properties: number;
  benchmark_unique_properties: number;
  overlap_percent: number | null;
  unique_percent: number | null;
  benchmark_unique_percent: number | null;
  fresh_7d_count: number;
  avg_fields_populated: number | null;
  fail_rate: number | null;
  block_rate: number | null;
  median_duration_ms: number | null;
  captured_at: string;
}

/**
 * Fetch all adapters
 */
export async function fetchAdapters(): Promise<AdapterRecord[]> {
  const { data, error } = await (supabase as any)
    .from('adapters')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching adapters:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch adapter by ID
 */
export async function fetchAdapterById(adapterId: string): Promise<AdapterRecord | null> {
  const { data, error } = await (supabase as any)
    .from('adapters')
    .select('*')
    .eq('id', adapterId)
    .single();

  if (error) {
    console.error('Error fetching adapter:', error);
    return null;
  }

  return data;
}

/**
 * Fetch adapter by slug
 */
export async function fetchAdapterBySlug(slug: string): Promise<AdapterRecord | null> {
  const { data, error } = await (supabase as any)
    .from('adapters')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    console.error('Error fetching adapter by slug:', error);
    return null;
  }

  return data;
}

/**
 * Update mutable fields on an adapter row.
 */
export async function updateAdapter(
  adapterId: string,
  patch: Partial<
    Pick<
      AdapterRecord,
      'status' | 'supported_states' | 'description' | 'default_config' | 'name'
    >
  >,
): Promise<AdapterRecord | null> {
  const { data, error } = await (supabase as any)
    .from('adapters')
    .update(patch)
    .eq('id', adapterId)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating adapter:', error);
    return null;
  }
  return data;
}

/**
 * Fetch active adapters only
 */
export async function fetchActiveAdapters(): Promise<AdapterRecord[]> {
  const { data, error } = await (supabase as any)
    .from('adapters')
    .select('*')
    .eq('status', 'active')
    .order('name');

  if (error) {
    console.error('Error fetching active adapters:', error);
    throw error;
  }

  return data || [];
}

export async function fetchProviderSettings(
  operationType: 'discovery' | 'detail' = 'detail',
  adapterSlug?: string,
  throwOnError = false,
): Promise<ProviderSettingsRecord | null> {
  let query = (supabase as any)
    .from('scrape_provider_settings')
    .select('*')
    .eq('operation_type', operationType)
    .limit(1);

  if (adapterSlug) {
    query = query.eq('scope', 'adapter').eq('adapter_slug', adapterSlug);
  } else {
    query = query.eq('scope', 'global').is('adapter_slug', null);
  }

  const { data, error } = await query.maybeSingle();
  if (error) {
    console.error('Error fetching provider settings:', error);
    if (throwOnError) throw error;
    return null;
  }
  return data;
}

export async function upsertProviderSettings(
  input: ProviderSettingsRecord,
): Promise<ProviderSettingsRecord | null> {
  const payload = {
    scope: input.scope,
    adapter_slug: input.scope === 'adapter' ? input.adapter_slug || null : null,
    operation_type: input.operation_type,
    use_auto: input.use_auto,
    enabled_providers: input.enabled_providers,
    provider_preference: input.provider_preference,
    retry_count: input.retry_count,
    timeout_ms: input.timeout_ms,
    metadata: input.metadata || {},
  };

  const { data, error } = await (supabase as any)
    .from('scrape_provider_settings')
    .upsert(payload, { onConflict: 'scope,operation_type,adapter_slug' })
    .select('*')
    .single();

  if (error) {
    console.error('Error upserting provider settings:', error);
    return null;
  }
  return data;
}

/**
 * Delete a provider-settings row, either global or per-adapter.
 * Returns true on success (including no-op deletes).
 */
export async function deleteProviderSettings(
  scope: 'global' | 'adapter',
  operationType: 'discovery' | 'detail',
  adapterSlug?: string,
): Promise<boolean> {
  let query = (supabase as any)
    .from('scrape_provider_settings')
    .delete()
    .eq('scope', scope)
    .eq('operation_type', operationType);

  if (scope === 'adapter') {
    if (!adapterSlug) {
      console.error('deleteProviderSettings: adapter_slug required for adapter scope');
      return false;
    }
    query = query.eq('adapter_slug', adapterSlug);
  } else {
    query = query.is('adapter_slug', null);
  }

  const { error } = await query;
  if (error) {
    console.error('Error deleting provider settings:', error);
    return false;
  }
  return true;
}

export async function runAdapterStatsMaterialization(input?: {
  triggerType?: 'manual' | 'scheduled';
  benchmarkAdapters?: string[];
  scopeFilters?: Record<string, unknown>;
}): Promise<{ success: boolean; runId?: string; error?: string }> {
  const { data, error } = await (supabase as any).rpc('compute_adapter_stats_snapshot', {
    p_trigger_type: input?.triggerType || 'manual',
    p_benchmark_adapters: input?.benchmarkAdapters || ['domain-com-au', 'realestate-com-au'],
    p_scope_filters: input?.scopeFilters || {},
  });

  if (error) {
    console.error('Error running adapter stats materialization:', error);
    return { success: false, error: error.message };
  }
  return { success: true, runId: data as string };
}

export async function fetchLatestAdapterStatsSnapshots(): Promise<AdapterStatsSnapshotRecord[]> {
  const { data: latestRun, error: runError } = await (supabase as any)
    .from('adapter_stats_runs')
    .select('id')
    .eq('status', 'completed')
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (runError || !latestRun?.id) {
    if (runError) console.error('Error fetching latest adapter stats run:', runError);
    return [];
  }

  const { data, error } = await (supabase as any)
    .from('adapter_stats_snapshots')
    .select('*')
    .eq('run_id', latestRun.id)
    .order('adapter_slug', { ascending: true });

  if (error) {
    console.error('Error fetching adapter stats snapshots:', error);
    return [];
  }
  return data || [];
}

export interface ScrapeQueueRowSummary {
  id: string;
  url: string;
  status: string;
  scraped_at: string | null;
  created_at: string;
  last_error: string | null;
}

/**
 * Recent scrape_queue rows for an adapter (incoming URL work).
 */
export async function fetchRecentScrapeQueueForAdapter(
  adapterId: string,
  limit = 50,
): Promise<ScrapeQueueRowSummary[]> {
  const { data, error } = await (supabase as any)
    .from("scrape_queue")
    .select("id, url, status, scraped_at, created_at, last_error")
    .eq("adapter_id", adapterId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching scrape_queue for adapter:", error);
    throw error;
  }
  return data ?? [];
}

export interface AdapterDetailStats {
  totalUrlsScraped: number;
  totalDiscoveryTasks: number;
  completedTasks: number;
  failedTasks: number;
  successRate: number;
  outOfRangeRate: number;
  lastRunAt: string | null;
}

export async function fetchAdapterDetailStats(
  adapterId: string,
): Promise<AdapterDetailStats> {
  const [queueResult, taskResult, oorResult, runResult] = await Promise.all([
    (supabase as any)
      .from("scrape_queue")
      .select("id", { count: "exact", head: true })
      .eq("adapter_id", adapterId)
      .eq("status", "completed"),
    (supabase as any)
      .from("discovery_tasks")
      .select("status")
      .eq("adapter_id", adapterId),
    (supabase as any)
      .from("scrape_queue")
      .select("id", { count: "exact", head: true })
      .eq("adapter_id", adapterId)
      .eq("status", "completed")
      .eq("is_out_of_range", true),
    (supabase as any)
      .from("scrape_queue")
      .select("scraped_at")
      .eq("adapter_id", adapterId)
      .eq("status", "completed")
      .order("scraped_at", { ascending: false })
      .limit(1),
  ]);

  const tasks = (taskResult.data ?? []) as Array<{ status: string }>;
  const completedTasks = tasks.filter((t) => t.status === "completed").length;
  const failedTasks = tasks.filter((t) => t.status === "failed").length;
  const totalScraped = queueResult.count ?? 0;
  const totalOor = oorResult.count ?? 0;
  const lastRow = (runResult.data ?? [])[0] as { scraped_at: string | null } | undefined;

  return {
    totalUrlsScraped: totalScraped,
    totalDiscoveryTasks: tasks.length,
    completedTasks,
    failedTasks,
    successRate: tasks.length > 0 ? completedTasks / tasks.length : 0,
    outOfRangeRate: totalScraped > 0 ? totalOor / totalScraped : 0,
    lastRunAt: lastRow?.scraped_at ?? null,
  };
}

export interface AdapterDailyScrapeStats {
  date: string;
  completed: number;
  failed: number;
  outOfRange: number;
}

export async function fetchAdapterDailyScrapeStats(
  adapterId: string,
  days = 30,
): Promise<AdapterDailyScrapeStats[]> {
  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data, error } = await (supabase as any)
    .from("scrape_queue")
    .select("status, scraped_at, created_at, is_out_of_range")
    .eq("adapter_id", adapterId)
    .gte("created_at", since.toISOString())
    .in("status", ["completed", "failed"])
    .order("created_at", { ascending: true });

  if (error || !data) return [];

  const rows = data as Array<{
    status: string;
    scraped_at: string | null;
    created_at: string;
    is_out_of_range: boolean;
  }>;

  const buckets = new Map<string, AdapterDailyScrapeStats>();
  for (const row of rows) {
    const d = (row.scraped_at ?? row.created_at).slice(0, 10);
    const bucket = buckets.get(d) ?? { date: d, completed: 0, failed: 0, outOfRange: 0 };
    if (row.status === "completed") bucket.completed++;
    if (row.status === "failed") bucket.failed++;
    if (row.is_out_of_range) bucket.outOfRange++;
    buckets.set(d, bucket);
  }

  return [...buckets.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export async function fetchAdapterRecentRuns(
  adapterId: string,
  limit = 5,
): Promise<Array<{
  id: string;
  job_id: string;
  job_name: string;
  status: string;
  phase: string;
  started_at: string;
  completed_at: string | null;
  urls_scraped: number;
}>> {
  const { data: tasks } = await (supabase as any)
    .from("discovery_tasks")
    .select("job_run_id")
    .eq("adapter_id", adapterId)
    .order("created_at", { ascending: false })
    .limit(50);

  if (!tasks?.length) return [];

  const runIds = [...new Set((tasks as Array<{ job_run_id: string }>).map((t) => t.job_run_id))].slice(0, limit);

  const { data: runs } = await (supabase as any)
    .from("job_runs")
    .select("id, job_id, status, phase, started_at, completed_at, urls_scraped, scrape_jobs!inner(name)")
    .in("id", runIds)
    .order("started_at", { ascending: false });

  if (!runs) return [];
  return (runs as Array<any>).map((r) => ({
    id: r.id,
    job_id: r.job_id,
    job_name: r.scrape_jobs?.name ?? "Unknown",
    status: r.status,
    phase: r.phase,
    started_at: r.started_at,
    completed_at: r.completed_at,
    urls_scraped: r.urls_scraped ?? 0,
  }));
}

export type AdapterVisibilityAction = "preview-detail" | "preview-discovery" | "preview-stored-detail";

export interface AdapterVisibilityRequest {
  action: AdapterVisibilityAction;
  adapterSlug: string;
  url: string;
  config?: Record<string, unknown>;
  /** Force one provider for a live preview (no fallback), e.g. "direct" to test a plain fetch. */
  provider?: "direct" | "firecrawl_scrape" | "zyte" | "brightdata";
}

export type AdapterVisibilityResponse =
  | {
      success: true;
      strategy: string;
      chain: string[];
      attempts: Array<Record<string, unknown>>;
      escalated?: boolean;
      result: Record<string, unknown>;
    }
  | {
      success: false;
      error: string;
      strategy?: string;
      chain?: string[];
      attempts?: Array<Record<string, unknown>>;
      escalated?: boolean;
    };

/**
 * Single-URL preview via edge function (Firecrawl / provider chain).
 */
export async function invokeAdapterVisibility(
  body: AdapterVisibilityRequest,
): Promise<AdapterVisibilityResponse> {
  const { data, error } = await supabase.functions.invoke("adapter-visibility", { body });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      try {
        const payload: unknown = await error.context.json();
        if (payload && typeof payload === "object" &&
            "success" in payload && payload.success === false &&
            "error" in payload && typeof payload.error === "string") {
          return payload as AdapterVisibilityResponse;
        }
      } catch {
        // Keep the transport error when the response has no JSON body.
      }
    }
    return { success: false, error: error.message };
  }
  if (data && typeof data === "object" && "success" in data) {
    return data as AdapterVisibilityResponse;
  }
  return { success: false, error: "Unexpected response from adapter-visibility" };
}

export async function fetchAdapterStatsRuns(limit = 10): Promise<Array<{
  id: string;
  trigger_type: string;
  status: string;
  benchmark_adapters: string[];
  scope_filters: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}>> {
  const { data, error } = await (supabase as any)
    .from('adapter_stats_runs')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching adapter stats runs:', error);
    return [];
  }
  return data || [];
}
